import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { usePopover } from "./usePopover";
import {
  CheckMark,
  ItemLabel,
  SearchableSelectPopover,
  SearchInput,
  SearchWrapper,
  SelectItem,
  SelectItemsContainer,
  SelectTrigger,
} from "./selectParts";

const Popover = styled(SearchableSelectPopover)(() => ({
  minWidth: "220px",
}));

const SectionHeader = styled("div")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: "13px",
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  letterSpacing: "-0.08px",
  padding: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[18]} ${theme.managementView.spacing[10]}`,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const SectionDivider = styled("div")(({ theme }) => ({
  height: "1px",
  backgroundColor: theme.managementView.colors.neutral[8],
  margin: `0 ${theme.managementView.spacing[12]}`,
}));

const SectionSpacer = styled("div")(() => ({
  height: "21px",
  display: "flex",
  alignItems: "center",
}));

export interface FilterSelectGroup {
  key: string;
  label: string;
  options: FilterSelectOption[];
}

export interface FilterSelectOption {
  key: string;
  label: string;
}

interface FilterSelectProps {
  groups: FilterSelectGroup[];
  value: string;
  searchPlaceholder?: string;
  onChange: (value: string) => void;
}

export const FilterSelect = ({ groups, value, searchPlaceholder, onChange }: FilterSelectProps) => {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();

  const selectedLabel = useMemo(() => {
    for (const group of groups) {
      const option = group.options.find((o) => o.key === value);

      if (option) return option.label;
    }
    return value;
  }, [groups, value]);

  const filteredGroups = useMemo(() => {
    if (!search) return groups;

    const query = search.toLowerCase();

    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter((o) => o.label.toLowerCase().includes(query)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, search]);

  const handleSelect = (key: string) => {
    onChange(key);
    close();
  };

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  return (
    <>
      <SelectTrigger ref={triggerRef} type="button" onClick={toggle}>
        {selectedLabel}
      </SelectTrigger>
      {isOpen &&
        createPortal(
          <Popover ref={popoverRef} style={position}>
            <SearchWrapper>
              <SearchInput
                ref={searchRef}
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrapper>

            <SelectItemsContainer>
              {filteredGroups.map((group, groupIndex) => (
                <div key={group.key}>
                  {groupIndex > 0 && (
                    <SectionSpacer>
                      <SectionDivider style={{ flex: 1 }} />
                    </SectionSpacer>
                  )}

                  <SectionHeader>{group.label}</SectionHeader>

                  {group.options.map((option) => (
                    <SelectItem key={option.key} onClick={() => handleSelect(option.key)}>
                      <ItemLabel>{option.label}</ItemLabel>
                      {option.key === value && <CheckMark />}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectItemsContainer>
          </Popover>,
          document.body,
        )}
    </>
  );
};
